import { HTMLAttributes } from 'react';

export type BadgeVariant =
  | 'primary'
  | 'accent'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  icon?: string;
  dot?: boolean;
  size?: 'sm' | 'md';
}

const variantMap: Record<BadgeVariant, string> = {
  primary:   'bg-primary-100 text-primary-800 border border-primary-200',
  accent:    'bg-accent-100 text-accent-900 border border-accent-200',
  secondary: 'bg-secondary-100 text-secondary-900 border border-secondary-200',
  success:   'bg-semantic-success-bg text-foreground-800 border border-semantic-success/30',
  warning:   'bg-semantic-warning-bg text-foreground-800 border border-semantic-warning/30',
  error:     'bg-semantic-error-bg text-foreground-800 border border-semantic-error/30',
  info:      'bg-semantic-info-bg text-foreground-800 border border-semantic-info/30',
  neutral:   'bg-background-100 text-foreground-600 border border-background-200',
};

const dotColorMap: Record<BadgeVariant, string> = {
  primary:   'bg-primary-500',
  accent:    'bg-accent-500',
  secondary: 'bg-secondary-500',
  success:   'bg-semantic-success',
  warning:   'bg-semantic-warning',
  error:     'bg-semantic-error',
  info:      'bg-semantic-info',
  neutral:   'bg-foreground-400',
};

export default function Badge({
  variant = 'neutral',
  icon,
  dot = false,
  size = 'md',
  children,
  className = '',
  ...rest
}: BadgeProps) {
  return (
    <span
      className={`
        inline-flex items-center gap-1.5 rounded-full font-label font-medium
        ${size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-xs px-2.5 py-1'}
        ${variantMap[variant]}
        ${className}
      `}
      {...rest}
    >
      {dot && (
        <span className={`w-1.5 h-1.5 rounded-full ${dotColorMap[variant]}`} />
      )}
      {icon && !dot && <i className={`${icon} text-xs`} />}
      {children}
    </span>
  );
}